import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import axiosInstance from '../../../main/initializers/axios';

interface ReservationLike {
  id: number;
  hotelId?: number;
  roomId?: number;
  roomNumber?: string;
  checkInDate?: string;
  checkOutDate?: string;
  guestName?: string;
  lockedPriceTotal?: number;
}

interface HotelPackageOption {
  id: number;
  hotelId: number;
  name: string;
  code: string;
  defaultPrice: number;
  isPerNight: boolean;
  isPerPerson: boolean;
}

interface ReservationPackageItem {
  id: number;
  hotelPackageId: number;
  quantity: number;
  unitPrice?: number;
  totalPrice: number;
  startDate: string;
  endDate: string;
  packageName?: string;
}

interface PackageFormState {
  id?: number;
  hotelPackageId: string;
  quantity: string;
  startDate: string;
  endDate: string;
}

type Props = {
  reservation?: ReservationLike;
  reservationId?: number;
  onSaved?: () => void;
};

const createInitialForm = (reservation?: ReservationLike): PackageFormState => ({
  hotelPackageId: '',
  quantity: '1',
  startDate: reservation.checkInDate?.split('T')[0] ?? '',
  endDate: reservation.checkOutDate?.split('T')[0] ?? '',
});

const ReservationPackages: React.FC<Props> = ({ reservation, reservationId: reservationIdProp, onSaved }) => {
  const { i18n } = useTranslation();
  const [packages, setPackages] = useState<ReservationPackageItem[]>([]);
  const [hotelPackages, setHotelPackages] = useState<HotelPackageOption[]>([]);
  const [form, setForm] = useState<PackageFormState>(() => createInitialForm(reservation));
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);
  const [isLoadingHotelPackages, setIsLoadingHotelPackages] = useState(false);

  const reservationId = reservation?.id ?? reservationIdProp ?? 0;
  const roomPrice = Number(reservation?.lockedPriceTotal ?? 0);
  const reservationCheckIn = reservation?.checkInDate?.split('T')[0] ?? '-';
  const reservationCheckOut = reservation?.checkOutDate?.split('T')[0] ?? '-';

  const initialForm = useMemo(() => createInitialForm(reservation), [reservation]);
  const formatPrice = useMemo(
    () => (value: number) =>
      new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value),
    [],
  );

  const loadReservationPackages = useCallback(async () => {
    if (!reservationId) {
      setPackages([]);
      return;
    }

    setIsLoadingPackages(true);
    try {
      const res = await axiosInstance.get(ApiConstants.reservation.listPackages, {
        params: { reservationId },
      });

      setPackages(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error(i18n.t('reservationDesk.failedToLoadPackages'));
    } finally {
      setIsLoadingPackages(false);
    }
  }, [reservationId]);

  const loadHotelPackages = useCallback(async () => {
    if (!reservation?.hotelId) {
      setHotelPackages([]);
      return;
    }

    setIsLoadingHotelPackages(true);
    try {
      const res = await axiosInstance.get(ApiConstants.hotel.packages, {
        params: { hotelId: reservation.hotelId },
      });

      setHotelPackages(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error(i18n.t('reservationDesk.failedToLoadHotelPackageCatalog'));
    } finally {
      setIsLoadingHotelPackages(false);
    }
  }, [reservation?.hotelId]);

  useEffect(() => {
    setForm(initialForm);
    loadReservationPackages();
    loadHotelPackages();
  }, [initialForm, loadHotelPackages, loadReservationPackages]);

  const resetForm = () => {
    setForm(initialForm);
  };

  const submitPackage = async () => {
    const hotelPackageId = Number(form.hotelPackageId);
    const quantity = Number(form.quantity);

    if (!hotelPackageId) {
      toast.error(i18n.t('reservationDesk.selectPackageRequired'));
      return;
    }

    if (!quantity || quantity <= 0) {
      toast.error(i18n.t('reservationDesk.packageQuantityPositive'));
      return;
    }

    if (!form.startDate || !form.endDate) {
      toast.error(i18n.t('reservationDesk.packageDatesRequired'));
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        id: form.id,
        reservationId,
        hotelPackageId,
        quantity,
        startDate: form.startDate,
        endDate: form.endDate,
      };

      if (form.id) {
        await axiosInstance.post(ApiConstants.reservation.updatePackage, payload);
      } else {
        await axiosInstance.post(ApiConstants.reservation.addPackage, payload);
      }

      resetForm();
      await loadReservationPackages();
      onSaved?.();
      toast.success(i18n.t(form.id ? 'reservationDesk.packageUpdatedSuccess' : 'reservationDesk.packageAddedSuccess'));
    } catch {
      toast.error(i18n.t(form.id ? 'reservationDesk.packageUpdateFailed' : 'reservationDesk.packageAddFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const deletePackage = async (id: number) => {
    try {
      await axiosInstance.delete(ApiConstants.reservation.deletePackage, {
        params: { id },
      });

      if (form.id === id) {
        resetForm();
      }

      await loadReservationPackages();
      onSaved?.();
      toast.success(i18n.t('reservationDesk.packageDeleted'));
    } catch {
      toast.error(i18n.t('reservationDesk.packageDeleteFailed'));
    }
  };

  const startEdit = (item: ReservationPackageItem) => {
    setForm({
      id: item.id,
      hotelPackageId: String(item.hotelPackageId ?? ''),
      quantity: String(item.quantity ?? 1),
      startDate: item.startDate?.split('T')[0] ?? '',
      endDate: item.endDate?.split('T')[0] ?? '',
    });
  };

  const selectedHotelPackage = hotelPackages.find((item) => item.id === Number(form.hotelPackageId));
  const selectedPackagePrice = Number(selectedHotelPackage?.defaultPrice ?? 0);

  const selectedDateRangeMultiplier = useMemo(() => {
    if (!form.startDate || !form.endDate) {
      return 1;
    }

    const start = new Date(`${form.startDate}T00:00:00`);
    const end = new Date(`${form.endDate}T00:00:00`);
    const diffInMs = end.getTime() - start.getTime();

    if (Number.isNaN(diffInMs) || diffInMs <= 0) {
      return 1;
    }

    return Math.max(1, Math.ceil(diffInMs / (1000 * 60 * 60 * 24)));
  }, [form.endDate, form.startDate]);

  const selectedPackageQuantity = Number(form.quantity) || 0;
  const selectedPackageMultiplier = selectedHotelPackage?.isPerNight ? selectedDateRangeMultiplier : 1;
  const calculatedTotal = selectedPackagePrice * selectedPackageQuantity * selectedPackageMultiplier;

  const existingPackagesTotal = useMemo(
    () =>
      packages.reduce((sum, item) => {
        if (form.id && item.id === form.id) {
          return sum;
        }

        return sum + Number(item.totalPrice ?? 0);
      }, 0),
    [form.id, packages],
  );

  const reservationTotalPreview = roomPrice + existingPackagesTotal + calculatedTotal;

  return (
    <div className="reservation-packages-shell">
      <div className="reservation-packages-summary">
        <div className="reservation-summary-item">
          <span>{i18n.t('reservationDesk.room')}</span>
          <strong>{reservation?.roomNumber ?? (reservation?.roomId ? `#${reservation.roomId}` : '-')}</strong>
        </div>
        <div className="reservation-summary-item">
          <span>{i18n.t('reservationDesk.guest')}</span>
          <strong>{reservation?.guestName || '-'}</strong>
        </div>
        <div className="reservation-summary-item">
          <span>{i18n.t('reservationDesk.checkIn')}</span>
          <strong>{reservationCheckIn}</strong>
        </div>
        <div className="reservation-summary-item">
          <span>{i18n.t('reservationDesk.checkOut')}</span>
          <strong>{reservationCheckOut}</strong>
        </div>
        <div className="reservation-summary-item reservation-summary-item-price">
          <span>{i18n.t('reservationDesk.roomPrice')}</span>
          <strong>{formatPrice(roomPrice)}</strong>
        </div>
        <div className="reservation-summary-item reservation-summary-item-total">
          <span>{i18n.t('reservationDesk.totalWithPackage')}</span>
          <strong>{formatPrice(reservationTotalPreview)}</strong>
        </div>
      </div>

      <div className="reservation-package-create-card">
        <div className="reservation-package-card-header">
          <div>
            <h4>{i18n.t(form.id ? 'reservationDesk.editReservationPackage' : 'reservationDesk.addPackageToReservation')}</h4>
            <p>{i18n.t('reservationDesk.selectExistingHotelPackage')}</p>
          </div>
          {form.id && <span className="reservation-package-tag">{i18n.t('reservationDesk.editing')}</span>}
        </div>

        <div className="reservation-package-form-grid">
          <div className="modal-field">
            <label>{i18n.t('reservationDesk.packageLabel')}</label>
            <select
              value={form.hotelPackageId}
              disabled={isSaving || isLoadingHotelPackages || hotelPackages.length === 0}
              onChange={(e) => setForm((prev) => ({ ...prev, hotelPackageId: e.target.value }))}
            >
              <option value="">{isLoadingHotelPackages ? i18n.t('reservationDesk.loadingPackages') : i18n.t('reservationDesk.selectPackage')}</option>
              {hotelPackages.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} ({item.code})
                </option>
              ))}
            </select>
          </div>

          <div className="modal-field">
            <label>{i18n.t('reservationDesk.quantity')}</label>
            <input
              type="number"
              min="1"
              value={form.quantity}
              disabled={isSaving}
              onChange={(e) => setForm((prev) => ({ ...prev, quantity: e.target.value }))}
            />
          </div>

          <div className="modal-field">
            <label>{i18n.t('reservationDesk.checkIn')}</label>
            <input
              type="date"
              value={form.startDate}
              disabled={isSaving}
              onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))}
            />
          </div>

          <div className="modal-field">
            <label>{i18n.t('reservationDesk.checkOut')}</label>
            <input type="date" value={form.endDate} disabled={isSaving} onChange={(e) => setForm((prev) => ({ ...prev, endDate: e.target.value }))} />
          </div>
        </div>

        <p className="reservation-package-helper">
          {selectedHotelPackage
            ? i18n.t(
                selectedHotelPackage.isPerNight ? 'reservationDesk.packageSelectionSummaryWithNights' : 'reservationDesk.packageSelectionSummary',
                {
                  packageName: selectedHotelPackage.name,
                  unitPrice: formatPrice(selectedPackagePrice),
                  quantity: selectedPackageQuantity,
                  nights: selectedDateRangeMultiplier,
                  totalPrice: formatPrice(calculatedTotal),
                  reservationTotal: formatPrice(reservationTotalPreview),
                },
              )
            : i18n.t('reservationDesk.noHotelPackagesAvailable')}
        </p>

        <div className="modal-actions">
          {form.id && (
            <button type="button" onClick={resetForm} disabled={isSaving}>
              {i18n.t('generic.cancel')}
            </button>
          )}
          <button type="button" onClick={submitPackage} disabled={isSaving}>
            {isSaving ? i18n.t('reservationDesk.saving') : i18n.t('generic.save')}
          </button>
        </div>
      </div>

      <div className="reservation-package-list">
        {packages.map((item) => (
          <div key={item.id} className="reservation-package-card">
            <div className="reservation-package-card-header">
              <div>
                <h4>{item.packageName ?? i18n.t('reservationDesk.packageFallbackName', { id: item.hotelPackageId })}</h4>
                <p>{i18n.t('reservationDesk.packageAssignedToReservation')}</p>
              </div>
              <span className="reservation-package-tag">{formatPrice(Number(item.totalPrice ?? 0))}</span>
            </div>

            <div className="reservation-package-meta">
              <span>{i18n.t('reservationDesk.quantityLabel', { value: item.quantity })}</span>
              <span>{i18n.t('reservationDesk.unitLabel', { value: formatPrice(Number(item.unitPrice ?? 0)) })}</span>
              <span>{i18n.t('reservationDesk.totalLabel', { value: formatPrice(Number(item.totalPrice ?? 0)) })}</span>
            </div>

            <div className="reservation-package-dates">
              <span>{i18n.t('reservationDesk.fromLabel', { value: item.startDate?.split('T')[0] ?? '-' })}</span>
              <span>{i18n.t('reservationDesk.toLabel', { value: item.endDate?.split('T')[0] ?? '-' })}</span>
            </div>

            <div className="reservation-package-card-actions">
              <button type="button" className="hotel-action-btn hotel-secondary-btn" onClick={() => startEdit(item)}>
                {i18n.t('actions.edit')}
              </button>
              <button type="button" className="hotel-action-btn hotel-cancel-btn" onClick={() => deletePackage(item.id)}>
                {i18n.t('generic.delete')}
              </button>
            </div>
          </div>
        ))}

        {!isLoadingPackages && packages.length === 0 && (
          <div className="reservation-package-card">
            <h4>{i18n.t('reservationDesk.noPackagesAttached')}</h4>
            <p className="reservation-package-helper">{i18n.t('reservationDesk.reservationHasNoPackagesYet')}</p>
          </div>
        )}

        {isLoadingPackages && (
          <div className="reservation-package-card">
            <h4>{i18n.t('reservationDesk.loadingPackages')}</h4>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReservationPackages;
